/**
 * @file useCarouselA11y.ts
 * Custom hook providing WCAG 2.1 AA compliant keyboard navigation and ARIA attribute mapping.
 *
 * Navigation rules per Material Design 3 Specs:
 * - Tab: Initial focus on first item (index 0).
 * - ArrowLeft / ArrowRight: Moves focus to previous or next item and scrolls it into view.
 * - ArrowUp / ArrowDown: Exits carousel navigation to move focus to adjacent page elements.
 * - Space / Enter: Activates the currently focused carousel item.
 *
 * @see docs/m3/carousel/Carousel_Accessibility_Material_Design_3.md
 */

import { useCallback, useState } from "react";

export interface UseCarouselA11yOptions {
	totalItems: number;
	onItemSelect?: (index: number) => void;
	onExitNavigation?: () => void;
	scrollToIndex?: (index: number) => void;
}

export function useCarouselA11y({
	totalItems,
	onItemSelect,
	onExitNavigation,
	scrollToIndex,
}: UseCarouselA11yOptions) {
	const [focusedIndex, setFocusedIndex] = useState<number>(0);

	const handleKeyDown = useCallback(
		(event: React.KeyboardEvent<HTMLDivElement>) => {
			if (totalItems === 0) return;

			switch (event.key) {
				case "ArrowRight":
				case "ArrowDown": {
					// ArrowRight moves to next item; ArrowDown exits or moves next depending on orientation
					if (event.key === "ArrowDown" && onExitNavigation) {
						onExitNavigation();
						return;
					}
					event.preventDefault();
					setFocusedIndex((prev) => {
						const next = Math.min(totalItems - 1, prev + 1);
						scrollToIndex?.(next);
						return next;
					});
					break;
				}
				case "ArrowLeft":
				case "ArrowUp": {
					if (event.key === "ArrowUp" && onExitNavigation) {
						onExitNavigation();
						return;
					}
					event.preventDefault();
					setFocusedIndex((prev) => {
						const next = Math.max(0, prev - 1);
						scrollToIndex?.(next);
						return next;
					});
					break;
				}
				case "Home": {
					event.preventDefault();
					setFocusedIndex(0);
					scrollToIndex?.(0);
					break;
				}
				case "End": {
					event.preventDefault();
					const lastIndex = totalItems - 1;
					setFocusedIndex(lastIndex);
					scrollToIndex?.(lastIndex);
					break;
				}
				case " ":
				case "Enter": {
					event.preventDefault();
					onItemSelect?.(focusedIndex);
					break;
				}
			}
		},
		[totalItems, focusedIndex, onItemSelect, onExitNavigation, scrollToIndex],
	);

	/** Generates WCAG compliant ARIA attributes for slide items */
	const getItemA11yProps = useCallback(
		(index: number, customLabel?: string) => {
			const itemNumber = index + 1;
			const defaultLabel = `Item ${itemNumber} of ${totalItems}`;

			return {
				role: "group" as const,
				"aria-roledescription": "slide",
				"aria-label": customLabel
					? `${customLabel} (${itemNumber} of ${totalItems})`
					: defaultLabel,
				tabIndex: index === focusedIndex ? 0 : -1,
				onFocus: () => setFocusedIndex(index),
			};
		},
		[focusedIndex, totalItems],
	);

	return {
		focusedIndex,
		setFocusedIndex,
		handleKeyDown,
		getItemA11yProps,
	};
}
